import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Users, DollarSign, Target } from 'lucide-react';

interface CalculatorCardProps {
  title: string;
  description: string;
  link: string;
}

const CustomerAcquisitionPage = () => {
  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/customer-acquisition`;
  const logoUrl = `${siteUrl}/logo.png`; // Replace with your actual logo URL

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Customer Acquisition Calculators: Master CAC, CPA & Lead Costs | Free Tools</title>
        <meta name="description" content="Master customer acquisition with our comprehensive calculator suite. Calculate CAC, CPA, CPL, conversion rates & more. Make data-driven decisions to reduce acquisition costs." />
        <meta name="keywords" content="customer acquisition calculator, CAC calculator, CPA calculator, cost per lead calculator, conversion rate calculator, customer acquisition metrics, marketing ROI tools" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free Customer Acquisition Calculators: Optimize Your Marketing ROI" />
        <meta property="og:description" content="Improve your marketing ROI with our free calculators. Measure CAC, CPA, conversion rates, and more. Optimize your customer acquisition strategies now!" />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free Customer Acquisition Calculators: Optimize Your Marketing ROI" />
        <meta property="twitter:description" content="Improve your marketing ROI with our free calculators. Measure CAC, CPA, conversion rates, and more. Optimize your customer acquisition strategies now!" />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <meta property="article:modified_time" content="2024-10-23" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Customer Acquisition Calculators",
            "description": "Optimize your customer acquisition strategies with our suite of calculators. Measure CAC, CPA, conversion rates, and more to improve your marketing ROI.",
            "url": pageUrl,
            "image": logoUrl,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": pageUrl
            },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [{
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": siteUrl
              }, {
                "@type": "ListItem",
                "position": 2,
                "name": "Customer Acquisition Calculators",
                "item": pageUrl
              }]
            },
            "hasPart": {
              "@type": "ItemList",
              "itemListElement": [
                {
                  "@type": "SoftwareApplication",
                  "name": "CPC Calculator",
                  "description": "Calculate Cost Per Click",
                  "url": `${siteUrl}/calculators/cost-per-click`,
                  "applicationCategory": "BusinessApplication",
                  "operatingSystem": "Any",
                  "offers": {
                    "@type": "Offer",
                    "price": "0",
                    "priceCurrency": "USD"
                  }
                },
                {
                  "@type": "SoftwareApplication",
                  "name": "CPA Calculator",
                  "description": "Calculate Cost Per Acquisition",
                  "url": `${siteUrl}/calculators/cost-per-acquisition`,
                  "applicationCategory": "BusinessApplication",
                  "operatingSystem": "Any",
                  "offers": {
                    "@type": "Offer",
                    "price": "0",
                    "priceCurrency": "USD"
                  }
                },
                {
                  "@type": "SoftwareApplication",
                  "name": "CAC Calculator",
                  "description": "Calculate Customer Acquisition Cost",
                  "url": `${siteUrl}/calculators/customer-acquisition-cost`,
                  "applicationCategory": "BusinessApplication",
                  "operatingSystem": "Any",
                  "offers": {
                    "@type": "Offer",
                    "price": "0",
                    "priceCurrency": "USD"
                  }
                }
              ]
            }
          })}
        </script>
      </Helmet>
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Customer Acquisition</h1>
      
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4">Customer Acquisition Calculators</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <CalculatorCard title="CPC Calculator" description="Calculate Cost Per Click" link="/calculators/cost-per-click" />
          <CalculatorCard title="CPA Calculator" description="Calculate Cost Per Acquisition" link="/calculators/cost-per-acquisition" />
          <CalculatorCard title="CPM Calculator" description="Calculate Cost Per Mille (1000 Impressions)" link="/calculators/cost-per-mille" />
          <CalculatorCard title="Cost Per Lead Calculator" description="Calculate Cost Per Lead" link="/calculators/cost-per-lead" />
          <CalculatorCard title="CAC Calculator" description="Calculate Customer Acquisition Cost" link="/calculators/customer-acquisition-cost" />
          <CalculatorCard title="Cost Per App Install Calculator" description="Calculate Cost Per App Installation" link="/calculators/cost-per-app-install" />
          <CalculatorCard title="Conversion Rate Calculator" description="Calculate Conversion Rate" link="/calculators/conversion-rate" />
        </div>
      </section>
    </div>
  );
};

const CalculatorCard: React.FC<CalculatorCardProps> = ({ title, description, link }) => (
  <Link to={link} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
    <h3 className="text-lg font-semibold text-gray-800 mb-2">{title}</h3>
    <p className="text-gray-600 mb-4">{description}</p>
    <span className="text-indigo-600 font-medium">Use Calculator</span>
  </Link>
);

export default CustomerAcquisitionPage;
